import DiceBox from '@3d-dice/dice-box';
import { Controller } from '@hotwired/stimulus';
import { Turbo } from '@hotwired/turbo-rails';
import { rollDiceNotation } from '../../lib/rolls/notation';
import { resolveGameViewClientId } from '../../lib/game_view_client';

export default class extends Controller {
  static classes = ['fading', 'playerCanvas'];

  static values = {
    activeClientId: String,
    campaignId: Number,
    canvasId: String,
    dice: String,
    diceColor: { type: String, default: '#2e8555' },
    diceSize: { type: Number, default: 5 },
    diceStyle: { type: String, default: 'default' },
    diceVisibleDuration: { type: Number, default: 10000 },
    instantRoll: { type: Boolean, default: false },
    maximumRollLength: { type: Number, default: 5 },
    rollId: Number,
    rollingPower: { type: Number, default: 5 },
    updateUrl: String
  };

  connect() {
    this.controllerConnected = true;
    this.#processCommand();
  }

  disconnect() {
    this.controllerConnected = false;
  }

  activeClientIdValueChanged() {
    if (this.controllerConnected) this.#processCommand();
  }

  sessionStateUpdated({ detail: { campaignId, activeClientId } }) {
    if (!this.hasCampaignIdValue || Number(campaignId) !== this.campaignIdValue) return;

    if (activeClientId) {
      this.activeClientIdValue = activeClientId;
    } else {
      this.element.removeAttribute('data-game-screen--dice-box-canvas-active-client-id-value');
    }
  }

  async #processCommand() {
    if (!this.#canProcessCommands()) return;
    if (!this.hasCanvasIdValue || !this.hasDiceValue || !this.hasUpdateUrlValue) return;

    const playerCanvas = this.#playerCanvas();
    if (!playerCanvas || playerCanvas.dataset.rollState) return;

    playerCanvas.dataset.rollState = 'processing';

    try {
      this.#showPlayerDice(playerCanvas);
      const diceBox = await this.#ensurePlayerDiceBox(playerCanvas);
      const result = await rollDiceNotation(diceBox, this.diceValue);

      if (!this.#canProcessCommands() || !playerCanvas.isConnected) {
        delete playerCanvas.dataset.rollState;
        this.#schedulePlayerFade(playerCanvas, 0);
        return;
      }

      playerCanvas.dataset.rollState = 'completed';
      this.#showOutcome(playerCanvas, result);
      this.#showDismissControl(playerCanvas);
      this.#schedulePlayerFade(playerCanvas);
      await this.#saveResult(this.updateUrlValue, result, this.#currentClientId());
    } catch (error) {
      delete playerCanvas.dataset.rollState;
      this.#schedulePlayerFade(playerCanvas, 0);
      this.#reportError(error);
    }
  }

  #isLiveScreen() {
    return (
      this.hasActiveClientIdValue &&
      this.activeClientIdValue.length > 0 &&
      this.#currentClientId() === this.activeClientIdValue
    );
  }

  #canProcessCommands() {
    return !this.hasCampaignIdValue || this.#isLiveScreen();
  }

  #currentClientId() {
    if (!this.hasCampaignIdValue) return null;

    return resolveGameViewClientId(this.campaignIdValue);
  }

  #playerCanvas() {
    const canvas = document.getElementById(this.canvasIdValue);
    if (!canvas) return null;
    this.#promotePrivateOverlay(canvas);

    const playerCanvasId = `${this.canvasIdValue}_roll_${this.rollIdValue}`;
    const existingCanvas = document.getElementById(playerCanvasId);
    if (existingCanvas?.dataset.rollState) return existingCanvas;
    existingCanvas?.remove();

    const playerCanvas = document.createElement('div');
    playerCanvas.id = playerCanvasId;
    playerCanvas.classList.add(this.playerCanvasClass);
    canvas.appendChild(playerCanvas);
    return playerCanvas;
  }

  #promotePrivateOverlay(canvas) {
    if (this.hasCampaignIdValue) return;

    const dialog = this.element.closest('dialog');
    const overlay = canvas.closest('.game-screen-dice-box-canvas--page-overlay');
    if (dialog && overlay && overlay.parentElement !== dialog) dialog.appendChild(overlay);
  }

  #ensurePlayerDiceBox(playerCanvas) {
    if (playerCanvas.__diceBoxPromise) return playerCanvas.__diceBoxPromise;

    const options = {
      container: `#${playerCanvas.id}`,
      id: `${playerCanvas.id}_dice`,
      assetPath: import.meta.env.BASE_URL,
      scale: this.diceSizeValue,
      settleTimeout: this.maximumRollLengthValue * 1000,
      suspendSimulation: this.instantRollValue,
      theme: this.diceStyleValue,
      themeColor: this.diceColorValue,
      throwForce: this.rollingPowerValue,
      transparent: true
    };
    const canvas = document.getElementById(this.canvasIdValue);
    const diceBox = canvas?.__diceBoxFactory?.(options) || new DiceBox(options);

    const initialization = diceBox
      .init()
      .then(() => diceBox)
      .catch((error) => {
        playerCanvas.__diceBoxPromise = null;
        throw error;
      });

    playerCanvas.__diceBoxPromise = initialization;
    return initialization;
  }

  #showPlayerDice(playerCanvas) {
    playerCanvas.style.removeProperty('transition-delay');
    playerCanvas.classList.remove(this.fadingClass);
  }

  #showOutcome(playerCanvas, result) {
    const outcome = document.createElement('div');
    outcome.classList.add('game-screen-dice-box-canvas__outcome');
    outcome.setAttribute('role', 'status');
    outcome.setAttribute('aria-label', `${this.diceValue} result: ${result}`);

    const notation = document.createElement('span');
    notation.classList.add('game-screen-dice-box-canvas__outcome-notation');
    notation.textContent = this.diceValue;

    const resultValue = document.createElement('span');
    resultValue.classList.add('game-screen-dice-box-canvas__outcome-result');
    resultValue.textContent = result;

    outcome.append(notation, resultValue);
    outcome.addEventListener('animationend', () => outcome.remove(), { once: true });
    playerCanvas.appendChild(outcome);
  }

  #showDismissControl(playerCanvas) {
    const canvas = playerCanvas.parentElement;
    let controls = canvas.querySelector('.game-screen-dice-box-canvas__controls');
    if (!controls) {
      controls = document.createElement('div');
      controls.className = 'game-screen-dice-box-canvas__controls';
      canvas.appendChild(controls);
    }

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'btn btn-sm btn-secondary';
    button.textContent = 'Hide 3D dice';
    button.setAttribute('aria-label', `Hide 3D dice: ${this.diceValue}`);
    button.setAttribute('aria-controls', playerCanvas.id);
    button.addEventListener(
      'click',
      (event) => {
        event.stopPropagation();
        this.#removePlayerCanvas(playerCanvas);
      },
      { once: true }
    );
    playerCanvas.__dismissButton = button;
    controls.appendChild(button);
  }

  #removePlayerCanvas(playerCanvas) {
    clearTimeout(playerCanvas.__fadeTimer);
    playerCanvas.removeEventListener('transitionend', playerCanvas.__fadeListener);
    delete playerCanvas.__fadeListener;
    delete playerCanvas.__fadeTimer;
    const controls = playerCanvas.__dismissButton?.parentElement;
    playerCanvas.__dismissButton?.remove();
    delete playerCanvas.__dismissButton;
    if (controls && !controls.children.length) controls.remove();
    playerCanvas.remove();
  }

  // Saved commands disconnect before their permanent canvases finish fading.
  // Keep cleanup with each canvas so dismissal and expiry share the same lifecycle.
  #schedulePlayerFade(playerCanvas, delay = this.diceVisibleDurationValue) {
    playerCanvas.style.transitionDelay = `${delay}ms`;
    if (playerCanvas.__fadeListener) {
      playerCanvas.removeEventListener('transitionend', playerCanvas.__fadeListener);
    }
    playerCanvas.__fadeListener = (event) => {
      if (event.target !== playerCanvas || event.propertyName !== 'opacity') return;

      this.#removePlayerCanvas(playerCanvas);
    };
    clearTimeout(playerCanvas.__fadeTimer);
    playerCanvas.__fadeTimer = setTimeout(() => this.#removePlayerCanvas(playerCanvas), delay + 1000);
    playerCanvas.addEventListener('transitionend', playerCanvas.__fadeListener);
    playerCanvas.classList.add(this.fadingClass);
  }

  async #saveResult(url, result, clientId) {
    const token = document.querySelector('meta[name="csrf-token"]')?.content;
    const body = { roll: { result } };
    if (clientId) body.game_view_session = { client_id: clientId };
    const response = await fetch(url, {
      method: 'PATCH',
      credentials: 'same-origin',
      headers: {
        Accept: 'text/vnd.turbo-stream.html, application/json',
        'Content-Type': 'application/json',
        'X-CSRF-Token': token
      },
      body: JSON.stringify(body)
    });

    if (!response.ok) throw new Error(`Unable to save dice result (${response.status}).`);

    if (response.headers.get('Content-Type')?.includes('text/vnd.turbo-stream.html')) {
      Turbo.renderStreamMessage(await response.text());
      return;
    }

    return (await response.json()).result;
  }

  #reportError(error) {
    console.error('Dice Box failed to process a roll.', error);
  }
}
